// src/types.ts
var DateType = /* @__PURE__ */ ((DateType2) => {
  DateType2["FULL"] = "FULL";
  DateType2["DATE_ONLY"] = "DATE_ONLY";
  DateType2["TIME_ONLY"] = "TIME_ONLY";
  return DateType2;
})(DateType || {});

// src/utils.ts
var keyAliases = {
  "years": "years",
  "year": "years",
  "y": "years",
  "Y": "years",
  "quarter": "quarters",
  "q": "quarters",
  "Q": "quarters",
  "months": "months",
  "month": "months",
  "M": "months",
  "weeks": "weeks",
  "week": "weeks",
  "w": "weeks",
  "days": "days",
  "day": "days",
  "d": "days",
  "D": "days",
  "hours": "hours",
  "hour": "hours",
  "h": "hours",
  "H": "hours",
  "minutes": "minutes",
  "minute": "minutes",
  "m": "minutes",
  "seconds": "seconds",
  "second": "seconds",
  "s": "seconds",
  "milliseconds": "milliseconds",
  "millisecond": "milliseconds",
  "S": "milliseconds"
};
function monthDiff(a, b) {
  if (a.day() < b.day()) {
    return -monthDiff(b, a);
  }
  const wholeMonthDiff = (b.year() - a.year()) * 12 + (b.month() - a.month());
  const anchor = a.clone().add(wholeMonthDiff, "months");
  const anchor2 = a.clone().add(wholeMonthDiff - 1, "months");
  const adjust = (b.valueOf() - anchor.valueOf()) / (anchor.valueOf() - anchor2.valueOf());
  return -(wholeMonthDiff + adjust) || 0;
}
function ensureNegative(value) {
  if (value < 0)
    return value;
  else if (value === 0)
    return -0;
  return value * -1;
}
function padNum(value, maxLength = 2) {
  return value.toString().padStart(maxLength, "0");
}

// src/idate.ts
var locale = "en-US";
var IDate = class _IDate {
  dateType;
  date;
  static setLocale(_locale) {
    locale = _locale;
  }
  static getLocale() {
    return locale;
  }
  static getWeekDayNames(notation) {
    const weekDayNames = [];
    const date = _IDate.now();
    let format = "";
    if (notation === "long")
      format = "dddd";
    else if (notation === "short")
      format = "dd";
    else if (notation === "narrow")
      format = "d";
    for (let ix = 0; ix < 7; ix++) {
      date.weekday(ix);
      weekDayNames.push(date.format(format));
    }
    return weekDayNames;
  }
  static now(dateType = "FULL" /* FULL */) {
    let value = /* @__PURE__ */ new Date();
    if (dateType === "DATE_ONLY" /* DATE_ONLY */)
      value = `${value.getFullYear()}-${value.getMonth() + 1}-${value.getDate()} 00:00:00`;
    return new _IDate(value, dateType);
  }
  constructor(value, dateFormat, dateType = "FULL" /* FULL */) {
    if (dateFormat) {
      if (Object.values(DateType).includes(dateFormat)) {
        this.dateType = dateFormat;
        dateFormat = void 0;
      } else
        this.dateType = dateType;
    } else
      this.dateType = dateType;
    this.parseValue(value, dateFormat);
  }
  add(value, key) {
    if (typeof value !== "number")
      return this.addMultiple(value);
    if (!key)
      return this;
    const dateKey = keyAliases[key];
    if (dateKey === "years")
      this.date.setFullYear(this.date.getFullYear() + value);
    else if (dateKey === "quarters")
      this.date.setMonth(this.date.getMonth() + 3 * value);
    else if (dateKey === "months")
      this.date.setMonth(this.date.getMonth() + value);
    else if (dateKey === "weeks")
      this.date.setDate(this.date.getDate() + value * 7);
    else if (dateKey === "days")
      this.date.setDate(this.date.getDate() + value);
    else if (dateKey === "hours")
      this.date.setHours(this.date.getHours() + value);
    else if (dateKey === "minutes")
      this.date.setMinutes(this.date.getMinutes() + value);
    else if (dateKey === "seconds")
      this.date.setSeconds(this.date.getSeconds() + value);
    else if (dateKey === "milliseconds")
      this.date.setMilliseconds(this.date.getMilliseconds() + value);
    return this;
  }
  addMultiple(params) {
    for (const [key, value] of Object.entries(params))
      this.add(value, key);
    return this;
  }
  subtract(value, key) {
    if (typeof value === "number")
      return this.add(ensureNegative(value), key);
    for (const [key2, val] of Object.entries(value))
      value[key2] = ensureNegative(val);
    return this.add(value);
  }
  set(params, value) {
    if (typeof params === "string") {
      if (value !== void 0) {
        const dateKey = keyAliases[params];
        this.setValue(dateKey, value);
      }
    } else {
      for (const [key, value2] of Object.entries(params)) {
        const dateKey = keyAliases[key];
        this.setValue(dateKey, value2);
      }
    }
    return this;
  }
  year(value) {
    if (value === void 0)
      return this.date.getFullYear();
    this.date.setFullYear(value);
    return this;
  }
  month(value) {
    if (value === void 0)
      return this.date.getMonth() + 1;
    this.date.setMonth(value - 1);
    return this;
  }
  week() {
    return this.getWeekNum();
  }
  day(value) {
    if (value === void 0)
      return this.date.getDate();
    this.date.setDate(value);
    return this;
  }
  hours(value) {
    if (value === void 0)
      return this.date.getHours();
    this.date.setHours(value);
    return this;
  }
  minutes(value) {
    if (value === void 0)
      return this.date.getMinutes();
    this.date.setMinutes(value);
    return this;
  }
  seconds(value) {
    if (value === void 0)
      return this.date.getSeconds();
    this.date.setSeconds(value);
    return this;
  }
  milliseconds(value) {
    if (value === void 0)
      return this.date.getMilliseconds();
    this.date.setMilliseconds(value);
    return this;
  }
  weekday(value) {
    if (value === void 0)
      return this.date.getDay();
    if (value < -6 || value > 6)
      throw new Error("Weekday must be (inclusive) between -6 and 6");
    let dayDiff;
    dayDiff = Math.abs(this.weekday() - Math.abs(value));
    if (!dayDiff)
      return this;
    if (value < 0 || value === -0)
      this.subtract(dayDiff, "days");
    else
      this.add(dayDiff, "days");
    return this;
  }
  quarter() {
    const month = this.month();
    return Math.ceil(month / 3);
  }
  toString() {
    return this.valueOf().toString();
  }
  valueOf() {
    return this.date.valueOf();
  }
  toISOString() {
    return this.date.toISOString();
  }
  format(format) {
    format = format ?? this.getDefaultFormat();
    const formatOptions = this.getFormatOptions(format);
    const formattedParts = new Intl.DateTimeFormat(locale, formatOptions).formatToParts(this.date);
    let formatted = "";
    while (format.length) {
      const char = format[0];
      let clearChar = true;
      if (char === "Y")
        formatted += this.findInParts(formattedParts, "year")?.value ?? "";
      else if (char === "Q")
        formatted += this.formatQuarter(format);
      else if (char === "M")
        formatted += this.findInParts(formattedParts, "month")?.value ?? "";
      else if (char === "D")
        formatted += this.findInParts(formattedParts, "day")?.value ?? "";
      else if (char === "d")
        formatted += this.findInParts(formattedParts, "weekday")?.value ?? "";
      else if (char === "H")
        formatted += this.findInParts(formattedParts, "hour")?.value ?? "";
      else if (char === "m")
        formatted += this.findInParts(formattedParts, "minute")?.value ?? "";
      else if (char === "s")
        formatted += this.findInParts(formattedParts, "second")?.value ?? "";
      else if (char === "S")
        formatted += this.findInParts(formattedParts, "fractionalSecond")?.value ?? "";
      else {
        formatted += char;
        clearChar = false;
      }
      if (clearChar) {
        const reg = new RegExp(char, "g");
        format = format.replace(reg, "");
      } else
        format = format.substring(1);
    }
    return formatted;
  }
  isBefore(other, granularity) {
    const otherClone = new _IDate(other);
    if (!granularity)
      return this < otherClone;
    const dateKey = keyAliases[granularity];
    if (dateKey === "milliseconds")
      return this < otherClone;
    const clone = this.clone();
    const params = this.buildCompareParams(dateKey);
    return clone.set(params) < otherClone.set(params);
  }
  isAtOrBefore(other, granularity) {
    const otherClone = new _IDate(other);
    if (!granularity)
      return this <= otherClone;
    const dateKey = keyAliases[granularity];
    if (dateKey === "milliseconds")
      return this <= otherClone;
    const clone = this.clone();
    const params = this.buildCompareParams(dateKey);
    return clone.set(params) <= otherClone.set(params);
  }
  isAfter(other, granularity) {
    const otherClone = new _IDate(other);
    if (!granularity)
      return this > otherClone;
    const dateKey = keyAliases[granularity];
    if (dateKey === "milliseconds")
      return this > otherClone;
    const clone = this.clone();
    const params = this.buildCompareParams(dateKey);
    return clone.set(params) > otherClone.set(params);
  }
  isAtOrAfter(other, granularity) {
    const otherClone = new _IDate(other);
    if (!granularity)
      return this >= otherClone;
    const dateKey = keyAliases[granularity];
    if (dateKey === "milliseconds")
      return this >= otherClone;
    const clone = this.clone();
    const params = this.buildCompareParams(dateKey);
    return clone.set(params) >= otherClone.set(params);
  }
  isAt(other, granularity) {
    const otherClone = new _IDate(other);
    if (!granularity)
      return this.valueOf() === otherClone.valueOf();
    const dateKey = keyAliases[granularity];
    if (dateKey === "milliseconds")
      return this.valueOf() === otherClone.valueOf();
    const clone = this.clone();
    const params = this.buildCompareParams(dateKey);
    return clone.set(params).valueOf() === otherClone.set(params).valueOf();
  }
  isBetween(from, to, granularity) {
    return this.isAfter(from, granularity) && this.isBefore(to, granularity);
  }
  isAtOrBetween(from, to, granularity) {
    return this.isAtOrAfter(from, granularity) && this.isAtOrBefore(to, granularity);
  }
  diff(other, granularity, asFloat = false) {
    const otherIDate = new _IDate(other);
    if (this.isAt(otherIDate, granularity))
      return 0;
    let diff = this.valueOf() - otherIDate.valueOf();
    if (granularity) {
      const dateKey = keyAliases[granularity];
      if (dateKey === "years")
        diff = monthDiff(this, otherIDate) / 12;
      else if (dateKey === "months")
        diff = monthDiff(this, otherIDate);
      else if (dateKey === "weeks")
        diff /= 6048e5;
      else if (dateKey === "days")
        diff /= 864e5;
      else if (dateKey === "hours")
        diff /= 36e5;
      else if (dateKey === "minutes")
        diff /= 6e4;
      else if (dateKey === "seconds")
        diff /= 1e3;
    }
    if (asFloat)
      return diff;
    if (diff < 0)
      return Math.ceil(diff);
    return Math.floor(diff);
  }
  absDiff(other, granularity, asFloat = false) {
    return Math.abs(this.diff(other, granularity, asFloat));
  }
  clone() {
    return new _IDate(this);
  }
  daysInMonth() {
    const lastDay = this.clone().add(1, "month").set("day", 1).subtract(1, "day");
    return lastDay.day();
  }
  isValid() {
    return !isNaN(this.date.valueOf());
  }
  maxTime() {
    this.set({ hours: 23, minutes: 59, seconds: 59, milliseconds: 999 });
    return this;
  }
  minTime() {
    this.set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
    return this;
  }
  startOf(key) {
    switch (keyAliases[key]) {
      case "years":
        return this.set({ months: 1, days: 1 }).minTime();
      case "quarters":
        const firstMonth = this.quarter() * 3 - 2;
        return this.set({ months: firstMonth, days: 1 }).minTime();
      case "months":
        return this.set({ days: 1 }).minTime();
      case "weeks":
        return this.day(this.day() - this.weekday()).minTime();
      case "days":
        return this.minTime();
      case "hours":
        return this.set({ minutes: 0, seconds: 0, milliseconds: 0 });
      case "minutes":
        return this.set({ seconds: 0, milliseconds: 0 });
      case "seconds":
        return this.milliseconds(0);
    }
    return this;
  }
  endOf(key) {
    switch (keyAliases[key]) {
      case "years":
        return this.set({ months: 12, days: 31 }).maxTime();
      case "quarters":
        const lastMonth = this.quarter() * 3;
        this.month(lastMonth);
        return this.set({ days: this.daysInMonth() }).maxTime();
      case "months":
        return this.set({ days: this.daysInMonth() }).maxTime();
      case "weeks":
        return this.day(this.day() - this.weekday() + 7).maxTime();
      case "days":
        return this.maxTime();
      case "hours":
        return this.set({ minutes: 59, seconds: 59, milliseconds: 999 });
      case "minutes":
        return this.set({ seconds: 59, milliseconds: 999 });
      case "seconds":
        return this.milliseconds(999);
    }
    return this;
  }
  formatQuarter(format) {
    const charCount = this.getNumChars(format, "Q");
    const quarter = this.quarter();
    if (charCount >= 2)
      return padNum(quarter);
    return quarter.toString();
  }
  buildCompareParams(granularity) {
    let params = {};
    if (granularity === "years")
      params = { months: 1, days: 1, hours: 0, minutes: 0, seconds: 0, milliseconds: 0 };
    else if (granularity === "months")
      params = { days: 1, hours: 0, minutes: 0, seconds: 0, milliseconds: 0 };
    else if (granularity === "days")
      params = { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 };
    else if (granularity === "hours")
      params = { minutes: 0, seconds: 0, milliseconds: 0 };
    else if (granularity === "minutes")
      params = { seconds: 0, milliseconds: 0 };
    else if (granularity === "seconds")
      params = { milliseconds: 0 };
    return params;
  }
  setValue(key, value) {
    if (key === "years")
      this.date.setFullYear(value);
    if (key === "quarters") {
      const firstMonth = value * 3 - 2;
      this.date.setMonth(firstMonth - 1);
    } else if (key === "months")
      this.date.setMonth(value - 1);
    else if (key === "days")
      this.date.setDate(value);
    else if (key === "hours")
      this.date.setHours(value);
    else if (key === "minutes")
      this.date.setMinutes(value);
    else if (key === "seconds")
      this.date.setSeconds(value);
    else if (key === "milliseconds")
      this.date.setMilliseconds(value);
  }
  parseValue(value, dateFormat) {
    if (!value)
      this.date = /* @__PURE__ */ new Date();
    else if (value instanceof _IDate)
      this.date = new Date(value.date);
    else if (value instanceof Date)
      this.date = new Date(value);
    else if (typeof value === "number")
      this.date = new Date(value);
    else {
      value = value.trim();
      if (dateFormat)
        this.parseCustom(value, dateFormat);
      else {
        this.date = new Date(value);
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          this.date.setHours(0);
          this.date.setMinutes(0);
          this.date.setSeconds(0);
          this.date.setMilliseconds(0);
        }
      }
    }
  }
  parseCustom(value, dateFormat) {
    const now = /* @__PURE__ */ new Date();
    let year = now.getFullYear();
    let month = now.getMonth();
    let day = now.getDate();
    let hour = 0;
    let minute = 0;
    let seconds = 0;
    let milliseconds = 0;
    while (dateFormat.length && value.length) {
      const nextChar = dateFormat[0];
      let charCount = this.getNumChars(dateFormat, nextChar);
      let formatCharCount = charCount;
      let clearValue = true;
      if (nextChar === "Y") {
        year = Number(value.substring(0, charCount));
        if (Number.isNaN(year))
          throw Error("Value does not match year format");
      } else if (nextChar === "M") {
        if (charCount === 4 || charCount === 3) {
          const reg = RegExp(`^[A-Za-z]+`);
          const monthMatch = reg.exec(value);
          if (!monthMatch)
            throw Error("Value does not match month format");
          const monthValue = monthMatch[0];
          const monthOption = charCount === 4 ? "long" : "short";
          month = this.getMonthNames(monthOption).indexOf(monthValue.toLowerCase());
          value = value.replace(monthValue, "");
          clearValue = false;
        } else if (charCount === 2) {
          month = Number(value.substring(0, charCount)) - 1;
          if (Number.isNaN(month))
            throw Error("Value does not match month format");
        } else {
          const monthMatch = /^\d{1,2}/.exec(value);
          if (!monthMatch)
            throw Error("Value does not match month format");
          const monthValue = monthMatch[0];
          month = Number(monthValue) - 1;
          const length = monthValue.length;
          value = value.substring(length);
          clearValue = false;
        }
      } else if (nextChar === "D") {
        if (charCount === 2) {
          const chars = [value.substring(0, 1), value.substring(1, 2)];
          let numString = "";
          if (/\d/.test(chars[0]))
            numString += chars[0];
          if (/\d/.test(chars[1]))
            numString += chars[1];
          if (!numString.length)
            throw Error("Value does not match date format");
          charCount = numString.length;
          day = Number(numString);
        } else {
          const dateMatch = /^\d{1,2}/.exec(value);
          if (!dateMatch)
            throw Error("Value does not match date format");
          const dateValue = dateMatch[0];
          day = Number(dateValue);
          const length = dateValue.length;
          value = value.substring(length);
          clearValue = false;
        }
      } else if (nextChar === "H") {
        if (charCount === 2) {
          hour = Number(value.substring(0, charCount));
          if (Number.isNaN(hour))
            throw Error("Value does not match hour format");
        } else {
          const hourMatch = /^\d{1,2}/.exec(value);
          if (!hourMatch)
            throw Error("Value does not match hour format");
          const hourValue = hourMatch[0];
          hour = Number(hourValue);
          const length = hourValue.length;
          value = value.substring(length);
          clearValue = false;
        }
      } else if (nextChar === "m") {
        if (charCount === 2) {
          minute = Number(value.substring(0, charCount));
          if (Number.isNaN(minute))
            throw Error("Value does not match minute format");
        } else {
          const minuteMatch = /^\d{1,2}/.exec(value);
          if (!minuteMatch)
            throw Error("Value does not match minute format");
          const minuteValue = minuteMatch[0];
          minute = Number(minuteValue);
          const length = minuteValue.length;
          value = value.substring(length);
          clearValue = false;
        }
      } else if (nextChar === "s") {
        if (charCount === 2) {
          seconds = Number(value.substring(0, charCount));
          if (Number.isNaN(seconds))
            throw Error("Value does not match second format");
        } else {
          const secondMatch = /^\d{1,2}/.exec(value);
          if (!secondMatch)
            throw Error("Value does not match second format");
          const secondValue = secondMatch[0];
          seconds = Number(secondValue);
          const length = secondValue.length;
          value = value.substring(length);
          clearValue = false;
        }
      } else if (nextChar === "S") {
        const msMatch = /^\d+/.exec(value);
        if (!msMatch)
          throw Error("Value does not match millisecond format");
        const msValue = msMatch[0];
        milliseconds = Number(msValue);
        const length = msValue.length;
        value = value.substring(length);
        clearValue = false;
      }
      dateFormat = dateFormat.substring(formatCharCount);
      if (clearValue)
        value = value.substring(charCount);
    }
    this.date = new Date(year, month, day, hour, minute, seconds, milliseconds);
  }
  getNumChars(dateFormat, char) {
    let count = 0;
    for (const _char of dateFormat) {
      if (_char !== char)
        return count;
      count++;
    }
    return count;
  }
  getDefaultFormat() {
    if (this.dateType === "FULL" /* FULL */)
      return "YYYY-MM-DD HH:mm:ss.SSS";
    else if (this.dateType === "DATE_ONLY" /* DATE_ONLY */)
      return "YYYY-MM-DD";
    else
      return "HH:mm:ss.SSS";
  }
  findInParts(parts, partType) {
    return parts.find((part) => part.type === partType);
  }
  getFormatOptions(format) {
    const options = {};
    for (let i = 0; i < format.length; i++) {
      const char = format[i];
      if (char === "Y") {
        if (!!options.year)
          continue;
        options.year = this.getYearOption(format);
      } else if (char === "M") {
        if (!!options.month)
          continue;
        options.month = this.getMonthOption(format);
      } else if (char === "D") {
        if (!!options.day)
          continue;
        options.day = this.getDayOption(format);
      } else if (char === "d") {
        if (!!options.weekday)
          continue;
        options.weekday = this.getWeekdayOption(format);
      } else if (char === "H") {
        if (!!options.hour)
          continue;
        options.hour = this.getHourOption(format);
      } else if (char === "m") {
        if (!!options.minute)
          continue;
        options.minute = this.getMinuteOption(format);
      } else if (char === "s") {
        if (!!options.second)
          continue;
        options.second = this.getSecondOption(format);
      } else if (char === "S") {
        if (!!options.fractionalSecondDigits)
          continue;
        options.fractionalSecondDigits = this.getMillisecondOption(format);
      }
    }
    return options;
  }
  getYearOption(format) {
    if (format.includes("YYYY"))
      return "numeric";
    else
      return "2-digit";
  }
  getMonthOption(format) {
    if (format.includes("MMMM"))
      return "long";
    else if (format.includes("MMM"))
      return "short";
    else if (format.includes("MM"))
      return "2-digit";
    else
      return "numeric";
  }
  getDayOption(format) {
    if (format.includes("DD"))
      return "2-digit";
    return "numeric";
  }
  getWeekdayOption(format) {
    if (format.includes("ddd"))
      return "long";
    else if (format.includes("dd"))
      return "short";
    return "narrow";
  }
  getHourOption(format) {
    if (format.includes("HH"))
      return "2-digit";
    return "numeric";
  }
  getMinuteOption(format) {
    if (format.includes("mm"))
      return "2-digit";
    return "numeric";
  }
  getSecondOption(format) {
    if (format.includes("ss"))
      return "2-digit";
    return "numeric";
  }
  getMillisecondOption(format) {
    if (format.includes("SSS"))
      return 3;
    else if (format.includes("SS"))
      return 2;
    else if (format.includes("S"))
      return 1;
  }
  getMonthNames(monthOption) {
    const monthNames = [];
    const date = /* @__PURE__ */ new Date("2000-01-01");
    for (let i = 0; i <= 11; i++) {
      const month = new Intl.DateTimeFormat(locale, { month: monthOption }).format(date).toLowerCase();
      monthNames.push(month);
      date.setMonth(date.getMonth() + 1);
    }
    return monthNames;
  }
  getWeekNum() {
    const date = new Date(this.date.getTime());
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    const week1 = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 864e5 - 3 + (week1.getDay() + 6) % 7) / 7);
  }
};
var idate_default = IDate;

// src/index.ts
var src_default = idate_default;
export {
  DateType,
  src_default as default
};
//# sourceMappingURL=index.mjs.map